// Copyright 2024 Sadiant Inc. All Rights Reserved. This software is subject to a license agreement. Unauthorized or unlicensed use is prohibited.
import { Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

const HIDE_ON_ROUTES = ['/contact', '/sign-up', '/apply', '/terms-of-service', '/privacy-policy', '/events'];

@Component({
  selector: 'app-social-share',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './social-share.component.html',
  styleUrls: ['./social-share.component.scss']
})
export class SocialShareComponent implements OnInit, OnDestroy {
  isVisible = false;
  isScrolled = false;
  showCopied = false;

  private encodedUrl = '';
  private encodedTitle = '';
  private sub = new Subscription();
  private isBrowser: boolean;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.sub.add(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
        const path = this.router.url.split('?')[0];
        this.isVisible = !HIDE_ON_ROUTES.some(r => path === r);
        if (this.isBrowser) {
          this.encodedUrl = encodeURIComponent(this.document.location.href);
          this.encodedTitle = encodeURIComponent(this.document.title);
        }
      })
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  @HostListener('window:scroll')
  onScroll() {
    if (this.isBrowser) {
      this.isScrolled = window.scrollY > 400;
    }
  }

  share(platform: 'linkedin' | 'twitter' | 'facebook') {
    if (!this.isBrowser) return;
    const urls: Record<string, string> = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${this.encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${this.encodedUrl}&text=${this.encodedTitle}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${this.encodedUrl}`
    };
    window.open(urls[platform], '_blank', 'noopener,width=620,height=500');
  }

  copyLink() {
    if (!this.isBrowser) return;
    navigator.clipboard.writeText(this.document.location.href).then(() => {
      this.showCopied = true;
      setTimeout(() => (this.showCopied = false), 2000);
    });
  }
}
